/**
 * DeadlinesPanel
 * Panel showing all period deadlines (book-in, verify, submit)
 * Pattern: Organism - composes molecules and atoms to display deadline information
 */

import {
  Box,
  Typography,
  Stack,
  Chip,
  Divider,
  Alert,
  Skeleton,
  IconButton,
  Tooltip,
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import { format, differenceInDays } from 'date-fns';
import { useGetPotPeriods } from '../hooks/useGetPotPeriods';
import { PotPeriodSummaryDto } from '../api/potPeriodsApi';
import { useMemo, useState, useCallback } from 'react';
import { FeatureSwitchGate, Features } from '@billda/features-ui';
import { TimeShiftDialog } from './TimeShiftDialog';
import { useDeadlineTimeShift } from '../hooks/useDeadlineTimeShift';
import { useEffectiveTime } from '../../development/hooks/useEffectiveTime';

interface DeadlinesPanelProps {
  currentYear: number;
}

interface DeadlineItemProps {
  label: string;
  deadline: string;
  icon: React.ReactNode;
  effectiveNow: Date;
  onGoWithin?: () => void;
  onPassDeadline?: () => void;
}

function DeadlineItem({
  label,
  deadline,
  icon,
  effectiveNow,
  onGoWithin,
  onPassDeadline,
}: DeadlineItemProps) {
  const deadlineDate = useMemo(() => new Date(deadline), [deadline]);
  const isOverdue = useMemo(
    () => deadlineDate < effectiveNow,
    [deadlineDate, effectiveNow]
  );
  const daysUntil = useMemo(
    () => differenceInDays(deadlineDate, effectiveNow),
    [deadlineDate, effectiveNow]
  );

  const getRelativeTimeText = () => {
    if (isOverdue) {
      const daysAgo = Math.abs(daysUntil);
      if (daysAgo === 0) return 'today';
      if (daysAgo === 1) return '1 day ago';
      return `${daysAgo} days ago`;
    }

    if (daysUntil === 0) return 'today';
    if (daysUntil === 1) return 'tomorrow';
    return `in ${daysUntil} days`;
  };

  const getDeadlineColor = () => {
    if (isOverdue) return 'error.main';
    if (daysUntil <= 1) return 'warning.main';
    return 'text.secondary';
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        py: 1,
      }}
    >
      <Box sx={{ color: getDeadlineColor(), mt: 0.5 }}>{icon}</Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2" fontWeight={500}>
          {label}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {format(deadlineDate, 'MMM d, yyyy • h:mm a')}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: getDeadlineColor(),
            fontWeight: 500,
          }}
        >
          {getRelativeTimeText()}
        </Typography>
      </Box>
      <FeatureSwitchGate feature={Features.Debug.IsTimeManipulationEnabled}>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Go within deadline">
            <IconButton size="small" onClick={onGoWithin}>
              <EventAvailableIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Pass deadline">
            <IconButton size="small" onClick={onPassDeadline}>
              <EventBusyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </FeatureSwitchGate>
    </Box>
  );
}

interface TimeShiftDialogState {
  open: boolean;
  deadline: Date;
  mode: 'before' | 'after';
  label: string;
}

interface PeriodDeadlinesCardProps {
  period: PotPeriodSummaryDto;
  effectiveNow: Date;
}

function PeriodDeadlinesCard({
  period,
  effectiveNow,
}: PeriodDeadlinesCardProps) {
  const [dialogState, setDialogState] = useState<TimeShiftDialogState>({
    open: false,
    deadline: new Date(),
    mode: 'before',
    label: '',
  });

  const {
    shiftTime,
    isPending,
    isSettingTime,
    isTransitioning,
    isSuccess,
    reset,
  } = useDeadlineTimeShift();

  // Close dialog on success
  const handleClose = useCallback(() => {
    setDialogState(prev => ({ ...prev, open: false }));
    reset();
  }, [reset]);

  // Handle success - close dialog after brief delay
  if (isSuccess && dialogState.open) {
    setTimeout(() => {
      handleClose();
    }, 500);
  }

  const openDialog = useCallback(
    (deadline: string, mode: 'before' | 'after', label: string) => {
      setDialogState({
        open: true,
        deadline: new Date(deadline),
        mode,
        label,
      });
    },
    []
  );

  const handleConfirm = useCallback(
    (targetTime: Date) => {
      shiftTime(targetTime);
    },
    [shiftTime]
  );

  // Determine period status based on deadlines
  const periodStatus = useMemo(() => {
    const submitDeadline = new Date(period.submitByDeadline);
    if (submitDeadline < effectiveNow) return 'completed';
    return 'active';
  }, [period.submitByDeadline, effectiveNow]);

  return (
    <>
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          p: 2,
          backgroundColor: 'background.paper',
        }}
      >
        {/* Period Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
              Week {period.weekNumber} • {period.year}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {format(new Date(period.periodStart), 'MMM d')} –{' '}
              {format(new Date(period.periodEnd), 'MMM d, yyyy')}
            </Typography>
          </Box>
          {periodStatus === 'completed' ? (
            <Chip
              icon={<CheckCircleIcon />}
              label="Complete"
              size="small"
              color="success"
              variant="outlined"
            />
          ) : (
            <Chip
              icon={<PendingIcon />}
              label="Active"
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Deadlines */}
        <Stack spacing={1}>
          <DeadlineItem
            label="Book In By"
            deadline={period.bookInByDeadline}
            icon={<EventIcon fontSize="small" />}
            effectiveNow={effectiveNow}
            onGoWithin={() =>
              openDialog(period.bookInByDeadline, 'before', 'Book In By')
            }
            onPassDeadline={() =>
              openDialog(period.bookInByDeadline, 'after', 'Book In By')
            }
          />
          <DeadlineItem
            label="Verify By"
            deadline={period.verifyByDeadline}
            icon={<EventIcon fontSize="small" />}
            effectiveNow={effectiveNow}
            onGoWithin={() =>
              openDialog(period.verifyByDeadline, 'before', 'Verify By')
            }
            onPassDeadline={() =>
              openDialog(period.verifyByDeadline, 'after', 'Verify By')
            }
          />
          <DeadlineItem
            label="Submit By"
            deadline={period.submitByDeadline}
            icon={<EventIcon fontSize="small" />}
            effectiveNow={effectiveNow}
            onGoWithin={() =>
              openDialog(period.submitByDeadline, 'before', 'Submit By')
            }
            onPassDeadline={() =>
              openDialog(period.submitByDeadline, 'after', 'Submit By')
            }
          />
        </Stack>
      </Box>

      <TimeShiftDialog
        open={dialogState.open}
        onClose={handleClose}
        onConfirm={handleConfirm}
        deadline={dialogState.deadline}
        mode={dialogState.mode}
        deadlineLabel={dialogState.label}
        isPending={isPending}
        isSettingTime={isSettingTime}
        isTransitioning={isTransitioning}
      />
    </>
  );
}

export function DeadlinesPanel({ currentYear }: DeadlinesPanelProps) {
  const { data, isLoading, error } = useGetPotPeriods({
    year: currentYear,
    includeEmpty: true,
  });
  const { effectiveNow } = useEffectiveTime();

  // Loading state
  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Skeleton variant="rectangular" height={200} />
          <Skeleton variant="rectangular" height={200} />
          <Skeleton variant="rectangular" height={200} />
        </Stack>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Failed to load period deadlines. Please try again.
        </Alert>
      </Box>
    );
  }

  // No data state
  if (!data || data.periods.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">No periods found for {currentYear}.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        {/* Header */}
        <Box>
          <Typography variant="h6" gutterBottom>
            All Deadlines
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View booking, verification, and submission deadlines for all periods
            in {currentYear}.
          </Typography>
        </Box>

        {/* Period Cards */}
        <Stack spacing={2}>
          {data.periods.map(period => (
            <PeriodDeadlinesCard
              key={period.id}
              period={period}
              effectiveNow={effectiveNow}
            />
          ))}
        </Stack>
      </Stack>
    </Box>
  );
}
